import { z } from "zod";

// ---------------------------------------------------------------------------
// 1:1 talks ("Samtal 1:1", "Kommande samtal")
//
// A scheduled or held conversation between a manager and a team member, with
// both agendas and the manager's private notes. Stored at:
//   Businesses/{businessId}/talks/{talkId}
//
// `privateNotes` is only ever returned to `managerUid`; the server strips it
// for everyone else.
// ---------------------------------------------------------------------------

export const TalkStatus = z.enum(["requested", "scheduled", "done", "cancelled"]);
export type TalkStatus = z.infer<typeof TalkStatus>;

// ---------------------------------------------------------------------------
// Anser talking points (GRA-201, part of GRA-154)
//
// 3-5 AI-suggested agenda points for an upcoming talk, generated from the
// member's profile (plain-language only, never raw cognition/personality
// scores), KPIs against target, recent pulse/check-in and open actions.
// Stored on the talk record so they are generated once (on booking, or on
// request via "Uppdatera förslag") rather than on every view. Human in the
// loop: the manager accepts, edits or drops each point; an accepted point's
// (possibly edited) text is appended to `agendaManager`, never applied
// automatically.
// ---------------------------------------------------------------------------

/** What kind of data a suggested point drew on - shown as a source chip. */
export const TalkAgendaSourceKind = z.enum([
  "kpi",
  "pulse",
  "actions",
  "assessment",
  "weeksSince1on1",
  "goal",
]);
export type TalkAgendaSourceKind = z.infer<typeof TalkAgendaSourceKind>;

export const TalkAgendaSourceRef = z.object({
  kind: TalkAgendaSourceKind,
  /** Plain-language label for the source chip, e.g. "KPI-läge". */
  label: z.string().min(1),
});
export type TalkAgendaSourceRef = z.infer<typeof TalkAgendaSourceRef>;

export const SuggestedAgendaPointStatus = z.enum(["pending", "accepted", "dropped"]);
export type SuggestedAgendaPointStatus = z.infer<typeof SuggestedAgendaPointStatus>;

export const SuggestedAgendaPoint = z.object({
  id: z.string().min(1),
  text: z.string().min(1).max(300),
  sourceRefs: z.array(TalkAgendaSourceRef).default([]),
  status: SuggestedAgendaPointStatus.default("pending"),
});
export type SuggestedAgendaPoint = z.infer<typeof SuggestedAgendaPoint>;

export interface TalkRecord {
  id: string;
  businessId: string;
  /** Roster member id (salesTeamsMembers). */
  memberId: string;
  managerUid: string | null;
  salesTeamId: string | null;
  /** ISO datetime. */
  scheduledAt: string | null;
  title: string;
  agendaManager: string[];
  agendaEmployee: string[];
  outcome: string | null;
  /** Manager-only; null for everyone else. */
  privateNotes: string | null;
  status: TalkStatus;
  requestedByUid: string | null;
  /** Anser's suggested talking points (GRA-201); regenerated only on request. */
  suggestedAgenda: SuggestedAgendaPoint[];
  /** When `suggestedAgenda` was last (re)generated; null before the first run. */
  suggestedAgendaGeneratedAt: string | null;
  /** Prompt version that produced the current `suggestedAgenda`. */
  suggestedAgendaPromptVersion: number | null;
  /**
   * Event id on the booking manager's personal Google Calendar (GRA-204, via
   * Composio), or null when there is none - no connection, the manager
   * declined "Lägg i kalendern", or creation failed. Rescheduling or
   * cancelling the talk updates or removes this event; a sync failure never
   * blocks the talk write itself.
   */
  calendarEventId: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export const ListTalksInput = z.object({
  businessId: z.string().min(1),
  memberId: z.string().min(1).optional(),
  /** Only talks scheduled from now on (requested or scheduled). */
  upcoming: z.boolean().optional(),
});
export type ListTalksInput = z.infer<typeof ListTalksInput>;

export const CreateTalkInput = z.object({
  businessId: z.string().min(1),
  memberId: z.string().min(1),
  scheduledAt: z.string().datetime({ offset: true }).nullable().optional(),
  title: z.string().min(1).max(160),
  agendaManager: z.array(z.string().min(1).max(300)).default([]),
  agendaEmployee: z.array(z.string().min(1).max(300)).default([]),
  status: TalkStatus.default("scheduled"),
  /**
   * "Lägg i kalendern" (GRA-204): best-effort, never blocks the talk itself.
   * Ignored when there is no `scheduledAt` - nothing to put on a calendar yet.
   */
  addToCalendar: z.boolean().default(false),
});
export type CreateTalkInput = z.infer<typeof CreateTalkInput>;

export const UpdateTalkInput = z.object({
  businessId: z.string().min(1),
  talkId: z.string().min(1),
  scheduledAt: z.string().datetime({ offset: true }).nullable().optional(),
  title: z.string().min(1).max(160).optional(),
  agendaManager: z.array(z.string().min(1).max(300)).optional(),
  agendaEmployee: z.array(z.string().min(1).max(300)).optional(),
  outcome: z.string().max(2000).nullable().optional(),
  privateNotes: z.string().max(4000).nullable().optional(),
  status: TalkStatus.optional(),
});
export type UpdateTalkInput = z.infer<typeof UpdateTalkInput>;

export const RequestMeetingInput = z.object({
  businessId: z.string().min(1),
  memberId: z.string().min(1),
  title: z.string().min(1).max(160).optional(),
  /** Preferred ISO datetime, when the requester picked a slot. */
  scheduledAt: z.string().datetime({ offset: true }).nullable().optional(),
  topic: z.string().max(500).nullable().optional(),
  /**
   * "Lägg i kalendern" (GRA-204): best-effort, never blocks the talk itself.
   * Ignored when there is no `scheduledAt` - nothing to put on a calendar yet.
   */
  addToCalendar: z.boolean().default(false),
});
export type RequestMeetingInput = z.infer<typeof RequestMeetingInput>;

/** "Uppdatera förslag" — regenerates `suggestedAgenda` from current data. */
export const RegenerateSuggestedAgendaInput = z.object({
  businessId: z.string().min(1),
  talkId: z.string().min(1),
});
export type RegenerateSuggestedAgendaInput = z.infer<
  typeof RegenerateSuggestedAgendaInput
>;

/**
 * Human-in-the-loop decision on one suggested point. `accept` appends
 * `text` (defaulting to the point's own text when omitted, i.e. editing then
 * accepting in one call) to `agendaManager`; `drop` discards it. Either way
 * the point's `status` in `suggestedAgenda` is updated, never regenerated.
 */
export const DecideSuggestedAgendaPointInput = z.object({
  businessId: z.string().min(1),
  talkId: z.string().min(1),
  pointId: z.string().min(1),
  decision: z.enum(["accept", "drop"]),
  /** Edited text to use instead of the suggested text; only meaningful with "accept". */
  text: z.string().min(1).max(300).optional(),
});
export type DecideSuggestedAgendaPointInput = z.infer<
  typeof DecideSuggestedAgendaPointInput
>;
